/**
 * AdsManagerPage — AI Marketing Platform for Meta Ads.
 * Dashboard (spend, leads, CPL), campaign control (pause/budget),
 * AI recommendations and AI campaign creation (always created PAUSED).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import AdCreativeEditor from "@/components/AdCreativeEditor";
import MetaAutopilotPanel from "@/components/MetaAutopilotPanel";
import {
  Target, Loader2, TrendingUp, MousePointerClick, Eye, Coins, UserCheck,
  Pause, Play, Sparkles, RefreshCw, ChevronDown, ChevronRight, AlertTriangle,
  PencilLine, Check, X, Megaphone, Lightbulb, Lock, Power, History, Undo2, ShieldCheck,
  Pencil, Crosshair,
} from "lucide-react";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n, cur = "CHF") =>
  n === null || n === undefined ? "—" : `${cur} ${Number(n).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("de-CH"));

const STATUS_BADGE = {
  ACTIVE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  PAUSED: "bg-amber-100 text-amber-700 border-amber-200",
  ARCHIVED: "bg-gray-100 text-gray-500 border-gray-200",
  DELETED: "bg-gray-100 text-gray-500 border-gray-200",
  IN_PROCESS: "bg-blue-100 text-blue-700 border-blue-200",
  WITH_ISSUES: "bg-red-100 text-red-700 border-red-200",
};

const OBJECTIVE_LABELS = {
  OUTCOME_TRAFFIC: "Traffic",
  OUTCOME_AWARENESS: "Bekanntheit",
  OUTCOME_ENGAGEMENT: "Interaktionen",
  OUTCOME_LEADS: "Leads",
  OUTCOME_SALES: "Verkäufe",
  OUTCOME_APP_PROMOTION: "App",
};

const SEVERITY_STYLE = {
  high: { cls: "border-red-300 bg-red-50", icon: "🔴" },
  medium: { cls: "border-amber-300 bg-amber-50", icon: "🟡" },
  info: { cls: "border-blue-200 bg-blue-50", icon: "💡" },
};

function KpiCard({ testid, icon: Icon, label, value, sub }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 text-xs text-[var(--studio-text-muted)]">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        <div className="text-xl font-bold mt-1">{value}</div>
        {sub && <div className="text-[11px] text-[var(--studio-text-muted)] mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function BudgetEditor({ objectId, current, currency, companyId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/ads/objects/${objectId}/budget`,
        { company_id: companyId, daily_budget: parseFloat(val) },
        { withCredentials: true });
      toast.success(`Tagesbudget auf ${fmtMoney(parseFloat(val), currency)} gesetzt`);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Budget konnte nicht geändert werden");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        data-testid={`edit-budget-${objectId}`}
        onClick={() => { setVal(current || ""); setEditing(true); }}
        className="inline-flex items-center gap-1 text-xs hover:underline"
        title="Tagesbudget ändern"
      >
        {current ? fmtMoney(current, currency) : "Kein Tagesbudget"}
        <PencilLine className="w-3 h-3 opacity-60" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <Input
        data-testid={`budget-input-${objectId}`}
        type="number" min="1" step="0.5" value={val}
        onChange={(e) => setVal(e.target.value)}
        className="h-7 w-24 text-xs"
      />
      <Button size="icon" className="h-7 w-7" disabled={saving || !val} onClick={save} data-testid={`budget-save-${objectId}`}>
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
      </Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(false)}>
        <X className="w-3 h-3" />
      </Button>
    </span>
  );
}

function StatusToggle({ objectId, status, companyId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isActive = status === "ACTIVE";
  const toggle = async () => {
    setBusy(true);
    try {
      const next = isActive ? "PAUSED" : "ACTIVE";
      await axios.post(`${API}/ads/objects/${objectId}/status`,
        { company_id: companyId, status: next }, { withCredentials: true });
      toast.success(next === "ACTIVE" ? "Aktiviert" : "Pausiert");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status konnte nicht geändert werden");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      data-testid={`toggle-status-${objectId}`}
      size="sm" variant={isActive ? "outline" : "default"}
      className="h-7 text-xs gap-1"
      disabled={busy}
      onClick={toggle}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : isActive ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
      {isActive ? "Pausieren" : "Aktivieren"}
    </Button>
  );
}

function InsightRow({ ins, currency }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[var(--studio-text-muted)]">
      <span>💰 {fmtMoney(ins?.spend, currency)}</span>
      <span>👁 {fmtNum(ins?.impressions)}</span>
      <span>🖱 {fmtNum(ins?.clicks)} Klicks</span>
      <span>CTR {ins?.ctr ? `${Number(ins.ctr).toFixed(2)}%` : "—"}</span>
      {ins?.leads > 0 && <span className="text-emerald-600 font-medium">✅ {ins.leads} Leads {ins.cost_per_lead ? `(${fmtMoney(ins.cost_per_lead, currency)}/Lead)` : ""}</span>}
    </div>
  );
}

function RenameButton({ objectId, current, companyId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!val.trim() || val === current) { setEditing(false); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ads/objects/${objectId}/rename`, { company_id: companyId, name: val.trim() }, { withCredentials: true });
      toast.success(r.data.message);
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Umbenennen fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  if (!editing) return (
    <button onClick={() => { setVal(current); setEditing(true); }} className="text-[var(--studio-text-muted)] hover:text-[var(--studio-text)]" title="Umbenennen" data-testid={`rename-${objectId}`}>
      <Pencil className="w-3 h-3" />
    </button>
  );
  return (
    <span className="inline-flex items-center gap-1">
      <Input className="h-6 text-xs w-56" value={val} onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && save()} autoFocus data-testid={`rename-input-${objectId}`} />
      <Button size="sm" className="h-6 px-2 text-xs" onClick={save} disabled={busy}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "OK"}</Button>
      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" onClick={() => setEditing(false)}>✕</Button>
    </span>
  );
}

function AudienceGeoFields({ companyId, value, onChange }) {
  const [geoQ, setGeoQ] = useState(value.geo_query || "");
  const [geoResults, setGeoResults] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => { if (value.geo_query) setGeoQ(value.geo_query); }, [value.geo_query]);
  const search = async () => {
    if (!geoQ.trim()) return;
    setSearching(true);
    try {
      const r = await axios.get(`${API}/ads/geo-search`, { params: { company_id: companyId, q: geoQ }, withCredentials: true });
      setGeoResults(r.data.results || []);
      if (!r.data.results?.length) toast.info("Kein Ort gefunden — anders schreiben (z.B. nur «Rapperswil»)");
    } catch (e) { toast.error(e.response?.data?.detail || "Ortssuche fehlgeschlagen"); }
    finally { setSearching(false); }
  };
  return (
    <div className="space-y-3 border border-[var(--studio-border)] rounded-lg p-3">
      <p className="text-xs font-semibold">🎯 Zielgruppe & Gebiet</p>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[11px] font-medium">Alter von</label>
          <Input type="number" min="18" max="65" className="mt-1 h-8 text-xs" value={value.age_min}
            onChange={(e) => onChange({ ...value, age_min: e.target.value })} data-testid="aud-age-min" />
        </div>
        <div>
          <label className="text-[11px] font-medium">Alter bis</label>
          <Input type="number" min="18" max="65" className="mt-1 h-8 text-xs" value={value.age_max}
            onChange={(e) => onChange({ ...value, age_max: e.target.value })} data-testid="aud-age-max" />
        </div>
        <div>
          <label className="text-[11px] font-medium">Geschlecht</label>
          <Select value={value.genders} onValueChange={(v) => onChange({ ...value, genders: v })}>
            <SelectTrigger className="mt-1 h-8 text-xs" data-testid="aud-gender"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="female">Frauen</SelectItem>
              <SelectItem value="male">Männer</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-2">
        {[{ v: "country", l: "🇨🇭 Ganzes Land" }, { v: "radius", l: "📍 Ort + Umkreis" }].map((o) => (
          <Button key={o.v} type="button" size="sm" variant={value.geo_mode === o.v ? "default" : "outline"}
            className="h-7 text-xs" onClick={() => onChange({ ...value, geo_mode: o.v })} data-testid={`geo-mode-${o.v}`}>
            {o.l}
          </Button>
        ))}
      </div>
      {value.geo_mode === "radius" && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input className="h-8 text-xs" placeholder="Ort suchen, z.B. Rapperswil" value={geoQ}
              onChange={(e) => setGeoQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              data-testid="geo-city-search" />
            <Button size="sm" className="h-8 text-xs" onClick={search} disabled={searching} data-testid="geo-city-search-btn">
              {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : "Suchen"}
            </Button>
            <Input type="number" min="17" max="80" className="h-8 text-xs w-24" title="Umkreis km" value={value.radius_km}
              onChange={(e) => onChange({ ...value, radius_km: e.target.value })} data-testid="geo-radius-km" />
            <span className="text-[11px] self-center text-[var(--studio-text-muted)]">km</span>
          </div>
          {geoResults.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {geoResults.map((r) => (
                <button key={r.key} onClick={() => { onChange({ ...value, city_key: r.key, city_name: r.name }); setGeoResults([]); }}
                  className="text-[11px] border border-dashed border-[#2563EB] text-[#2563EB] rounded-full px-2 py-0.5 hover:bg-blue-50"
                  data-testid={`geo-city-opt-${r.key}`}>
                  📍 {r.name}{r.region ? `, ${r.region}` : ""} ({r.country_code})
                </button>
              ))}
            </div>
          )}
          {value.city_key && (
            <p className="text-[11px] text-emerald-600" data-testid="geo-city-selected">
              ✓ {value.city_name} + {value.radius_km} km Umkreis (Meta-Minimum 17 km)
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function TargetingDialog({ adset, companyId, open, onClose, onSaved }) {
  const [aud, setAud] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && adset) {
      setAud({
        age_min: adset.age_min || 18, age_max: adset.age_max || 65, genders: adset.genders || "all",
        geo_mode: adset.cities?.length ? "radius" : "country",
        city_key: adset.cities?.[0]?.key || null, city_name: adset.cities?.[0]?.name || "",
        radius_km: adset.cities?.[0]?.radius || 25,
      });
    }
  }, [open, adset]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ads/adsets/${adset.id}/targeting`, {
        company_id: companyId,
        age_min: parseInt(aud.age_min), age_max: parseInt(aud.age_max), genders: aud.genders,
        geo: aud.geo_mode === "radius" && aud.city_key
          ? { mode: "radius", city_key: aud.city_key, radius_km: parseInt(aud.radius_km) }
          : { mode: "country", country: "CH" },
      }, { withCredentials: true });
      toast.success(r.data.message);
      onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="targeting-dialog">
        <DialogHeader><DialogTitle>Zielgruppe & Gebiet — {adset?.name}</DialogTitle></DialogHeader>
        {aud && (
          <div className="space-y-3">
            <AudienceGeoFields companyId={companyId} value={aud} onChange={setAud} />
            <Button className="w-full" onClick={save} disabled={busy || (aud.geo_mode === "radius" && !aud.city_key)} data-testid="targeting-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Zielgruppe speichern
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CampaignRow({ c, companyId, currency, datePreset, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [targetEdit, setTargetEdit] = useState(null);
  const [creativeEdit, setCreativeEdit] = useState(null);

  const loadDetails = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (details) return;
    setLoadingDetails(true);
    try {
      const r = await axios.get(`${API}/ads/campaigns/${c.id}/details`, {
        params: { company_id: companyId, date_preset: datePreset }, withCredentials: true,
      });
      setDetails(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Details konnten nicht geladen werden");
      setExpanded(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <div className="border border-[var(--studio-border)] rounded-lg" data-testid={`campaign-row-${c.id}`}>
      <div className="p-3 flex flex-wrap items-center gap-3">
        <button onClick={loadDetails} className="shrink-0" data-testid={`expand-campaign-${c.id}`}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-[200px]">
          <div className="font-medium text-sm flex items-center gap-2">
            {c.name}
            <RenameButton objectId={c.id} current={c.name} companyId={companyId} onSaved={onChanged} />
            <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[c.effective_status] || STATUS_BADGE.PAUSED}`}>
              {c.effective_status}
            </Badge>
            <Badge variant="outline" className="text-[10px]">{OBJECTIVE_LABELS[c.objective] || c.objective}</Badge>
          </div>
          <InsightRow ins={c.insights} currency={currency} />
        </div>
        <div className="flex items-center gap-3">
          <BudgetEditor objectId={c.id} current={c.daily_budget} currency={currency} companyId={companyId} onSaved={onChanged} />
          <StatusToggle objectId={c.id} status={c.status} companyId={companyId} onChanged={onChanged} />
        </div>
      </div>
      {expanded && (
        <div className="border-t border-[var(--studio-border)] p-3 bg-[var(--studio-bg)] space-y-3">
          {loadingDetails && <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Lade Ad Sets & Anzeigen…</div>}
          {details && (
            <>
              <div>
                <div className="text-xs font-semibold mb-1.5 uppercase tracking-wide text-[var(--studio-text-muted)]">Ad Sets ({details.adsets.length})</div>
                {details.adsets.map((a) => (
                  <div key={a.id} className="flex flex-wrap items-center gap-3 py-1.5 border-b border-[var(--studio-border)] last:border-0" data-testid={`adset-row-${a.id}`}>
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-xs font-medium flex items-center gap-2 flex-wrap">
                        {a.name}
                        <Badge variant="outline" className={`text-[9px] ${STATUS_BADGE[a.effective_status] || ""}`}>{a.effective_status}</Badge>
                        <span className="text-[10px] text-[var(--studio-text-muted)]">
                          {a.cities?.length > 0
                            ? `📍 ${a.cities[0].name} + ${a.cities[0].radius} km`
                            : a.countries?.length > 0 ? `🌍 ${a.countries.join(", ")}` : ""}
                          {" · "}{a.age_min || 18}-{a.age_max || 65} J. · {a.genders === "male" ? "Männer" : a.genders === "female" ? "Frauen" : "Alle"}
                        </span>
                      </div>
                      <InsightRow ins={a.insights} currency={currency} />
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setTargetEdit(a)} data-testid={`adset-targeting-${a.id}`}>
                      <Crosshair className="w-3 h-3" /> Zielgruppe
                    </Button>
                    <BudgetEditor objectId={a.id} current={a.daily_budget} currency={currency} companyId={companyId} onSaved={onChanged} />
                    <StatusToggle objectId={a.id} status={a.status} companyId={companyId} onChanged={onChanged} />
                  </div>
                ))}
                {details.adsets.length === 0 && <div className="text-xs text-[var(--studio-text-muted)]">Keine Ad Sets</div>}
              </div>
              <div>
                <div className="text-xs font-semibold mb-1.5 uppercase tracking-wide text-[var(--studio-text-muted)]">Anzeigen ({details.ads.length})</div>
                {details.ads.map((a) => (
                  <div key={a.id} className="flex flex-wrap items-center gap-3 py-1.5 border-b border-[var(--studio-border)] last:border-0" data-testid={`ad-row-${a.id}`}>
                    {a.thumbnail_url && <img src={a.thumbnail_url} alt="" className="w-9 h-9 rounded object-cover shrink-0" />}
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-xs font-medium flex items-center gap-2">
                        {a.name}
                        <RenameButton objectId={a.id} current={a.name} companyId={companyId} onSaved={onChanged} />
                        <Badge variant="outline" className={`text-[9px] ${STATUS_BADGE[a.effective_status] || ""}`}>{a.effective_status}</Badge>
                      </div>
                      {a.headline && <div className="text-[11px] font-medium">«{a.headline}»{a.description ? ` — ${a.description}` : ""}</div>}
                      {a.body && <div className="text-[11px] text-[var(--studio-text-muted)] max-w-xl whitespace-pre-wrap">{a.body}</div>}
                      {a.link && (
                        <a href={a.link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#2563EB] hover:underline break-all" data-testid={`ad-link-${a.id}`}>
                          🔗 {a.link}
                        </a>
                      )}
                      <InsightRow ins={a.insights} currency={currency} />
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setCreativeEdit(a)} data-testid={`ad-edit-creative-${a.id}`}>
                      ✏️ Bearbeiten
                    </Button>
                    <StatusToggle objectId={a.id} status={a.status} companyId={companyId} onChanged={onChanged} />
                  </div>
                ))}
                {details.ads.length === 0 && <div className="text-xs text-[var(--studio-text-muted)]">Keine Anzeigen</div>}
              </div>
              {creativeEdit && (
                <AdCreativeEditor ad={creativeEdit} companyId={companyId}
                  onClose={() => setCreativeEdit(null)} onSaved={onChanged} />
              )}
              {details.demographics?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1.5 uppercase tracking-wide text-[var(--studio-text-muted)]">Wen hat die Anzeige erreicht? (Alter & Geschlecht)</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" data-testid={`demographics-${c.id}`}>
                    {details.demographics.slice(0, 8).map((d, i) => (
                      <div key={i} className="border border-[var(--studio-border)] rounded px-2 py-1 text-[11px] bg-white">
                        <b>{d.age}</b> · {d.gender === "male" ? "♂" : d.gender === "female" ? "♀" : "?"}
                        <div className="text-[var(--studio-text-muted)]">{d.reach || d.impressions} erreicht · {d.clicks} Klicks</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <TargetingDialog adset={targetEdit} companyId={companyId} open={!!targetEdit}
        onClose={() => setTargetEdit(null)} onSaved={() => setDetails(null)} />
    </div>
  );
}

function AiCreateDialog({ open, onOpenChange, companyId, currency, onCreated }) {
  const [objective, setObjective] = useState("OUTCOME_TRAFFIC");
  const [brief, setBrief] = useState("");
  const [budget, setBudget] = useState("10");
  const [landingUrl, setLandingUrl] = useState("");
  const [country, setCountry] = useState("CH");
  const [mediaId, setMediaId] = useState("none");
  const [media, setMedia] = useState([]);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [aud, setAud] = useState({ age_min: 18, age_max: 65, genders: "all", geo_mode: "country", city_key: null, city_name: "", radius_km: 25 });

  useEffect(() => {
    if (!open || !companyId) return;
    axios.get(`${API}/media`, { params: { company_id: companyId }, withCredentials: true })
      .then((r) => setMedia((r.data || []).filter((m) => (m.content_type || "").startsWith("image"))))
      .catch(() => {});
    axios.get(`${API}/companies`, { withCredentials: true }).then((r) => {
      const comp = (r.data || []).find((x) => x.id === companyId);
      if (comp?.geo_mode === "regional") {
        setAud((a) => ({ ...a, geo_mode: "radius", radius_km: Math.max(17, comp.geo_radius_km || 25), geo_query: (comp.geo_region || "").split(/[ ,/]/)[0] }));
      }
    }).catch(() => {});
  }, [open, companyId]);

  const create = async () => {
    setCreating(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/ads/ai/create-campaign`, {
        company_id: companyId,
        objective, brief,
        daily_budget: parseFloat(budget),
        landing_url: landingUrl,
        country,
        age_min: parseInt(aud.age_min) || 18,
        age_max: parseInt(aud.age_max) || 65,
        genders: aud.genders,
        geo: aud.geo_mode === "radius" && aud.city_key
          ? { mode: "radius", city_key: aud.city_key, city_name: aud.city_name, radius_km: parseInt(aud.radius_km) || 25 }
          : { mode: "country", country },
        media_id: mediaId !== "none" ? mediaId : null,
      }, { withCredentials: true });
      setResult(r.data);
      toast.success(r.data.message);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kampagne konnte nicht erstellt werden");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-create-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-500" /> KI-Kampagne erstellen</DialogTitle>
          <DialogDescription>
            Die KI schreibt die Werbetexte und legt Kampagne, Ad Set und Anzeige an — <strong>immer PAUSIERT</strong>, es läuft kein Geld ohne deine Freigabe.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="ai-create-success">
              ✅ {result.message}
            </div>
            <div className="text-xs space-y-1 bg-[var(--studio-bg)] rounded-lg p-3 border border-[var(--studio-border)]">
              <div><strong>Headline:</strong> {result.copy?.headline}</div>
              <div><strong>Text:</strong> {result.copy?.primary_text}</div>
            </div>
            <Button className="w-full" onClick={() => { setResult(null); onOpenChange(false); }} data-testid="ai-create-close-btn">Fertig</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Kampagnenziel</label>
              <Select value={objective} onValueChange={setObjective}>
                <SelectTrigger data-testid="objective-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="OUTCOME_TRAFFIC">🔗 Traffic — Besucher auf Website/Landingpage</SelectItem>
                  <SelectItem value="OUTCOME_AWARENESS">📣 Bekanntheit — maximale Reichweite</SelectItem>
                  <SelectItem value="OUTCOME_ENGAGEMENT">❤️ Interaktionen — Likes, Kommentare</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Briefing — was soll beworben werden? *</label>
              <Textarea
                data-testid="ai-brief-input"
                value={brief} onChange={(e) => setBrief(e.target.value)}
                placeholder="z.B. Neueröffnung unseres Fitness-Studios in Zürich, Sonderangebot: 3 Monate für CHF 99, Zielgruppe 25-45 Jahre"
                className="mt-1 min-h-[80px]"
                maxLength={800}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Tagesbudget ({currency}) *</label>
                <Input data-testid="ai-budget-input" type="number" min="1" step="0.5" value={budget} onChange={(e) => setBudget(e.target.value)} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium">Land</label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger data-testid="country-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CH">🇨🇭 Schweiz</SelectItem>
                    <SelectItem value="AT">🇦🇹 Österreich</SelectItem>
                    <SelectItem value="DE">🇩🇪 Deutschland</SelectItem>
                    <SelectItem value="LI">🇱🇮 Liechtenstein</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">Landing-URL *</label>
              <Input data-testid="ai-landing-url-input" value={landingUrl} onChange={(e) => setLandingUrl(e.target.value)} placeholder="https://deine-website.ch/angebot" className="mt-1" />
            </div>
            <AudienceGeoFields companyId={companyId} value={aud} onChange={setAud} />
            <div>
              <label className="text-xs font-medium">Bild aus Mediathek (optional)</label>
              <Select value={mediaId} onValueChange={setMediaId}>
                <SelectTrigger data-testid="ai-media-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Bild (Meta nutzt Link-Vorschau)</SelectItem>
                  {media.slice(0, 40).map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.filename || m.original_filename || m.id.slice(0, 8)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="ai-create-submit-btn"
              className="w-full gap-2"
              disabled={creating || !brief.trim() || !landingUrl.trim() || !budget}
              onClick={create}
            >
              {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> KI erstellt Kampagne…</> : <><Sparkles className="w-4 h-4" /> Kampagne erstellen (PAUSIERT)</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const PERM_LABELS = {
  create_campaign: "Kampagnen erstellen",
  create_ads: "Anzeigen erstellen",
  pause_activate: "Pausieren & Aktivieren",
  keywords: "Keywords & Negative Keywords",
  audience: "Zielgruppen & Geo",
  creative: "Creatives & Texte",
  recommendations: "Empfehlungen erstellen",
};
const LOCKED_LABELS = ["Tagesbudget", "Monatsbudget", "Billing", "Zahlungsmethode"];

function AiControlPanel({ companyId, onSettingsChange }) {
  const [settings, setSettings] = useState(null);
  const [actions, setActions] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [reverting, setReverting] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/companies/${companyId}/ai-settings`, { withCredentials: true }),
        axios.get(`${API}/ai/actions`, { params: { company_id: companyId, limit: 30 }, withCredentials: true }),
      ]);
      setSettings(s.data);
      onSettingsChange?.(s.data);
      setActions(a.data.items || []);
    } catch (e) { /* silent */ }
  }, [companyId, onSettingsChange]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try {
      const r = await axios.put(`${API}/companies/${companyId}/ai-settings`, patch, { withCredentials: true });
      setSettings(r.data);
      onSettingsChange?.(r.data);
      toast.success(patch.ai_enabled === false ? "KI-Marketing deaktiviert (Not-Aus)" : "Gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const revert = async (a) => {
    setReverting(a.id);
    try {
      await axios.post(`${API}/ai/actions/${a.id}/revert`, {}, { withCredentials: true });
      toast.success("Aktion rueckgaengig gemacht");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rueckgaengig fehlgeschlagen");
    } finally {
      setReverting(null);
    }
  };

  if (!settings) return null;
  return (
    <Card data-testid="ai-control-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#2563EB]" /> KI-Steuerung
          <Badge variant="outline" className={`text-[10px] ${settings.ai_enabled ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}`}>
            {settings.ai_enabled ? "AKTIV" : "NOT-AUS"}
          </Badge>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-xs font-normal">
            <Power className={`w-3.5 h-3.5 ${settings.ai_enabled ? "text-emerald-500" : "text-red-500"}`} />
            KI-Marketing
            <Switch
              data-testid="ai-enabled-switch"
              checked={settings.ai_enabled}
              onCheckedChange={(v) => save({ ai_enabled: v })}
            />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 mb-2">🟢 KI darf (konfigurierbar)</div>
            <div className="space-y-1.5">
              {Object.entries(PERM_LABELS).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-xs" data-testid={`ai-perm-${k}`}>
                  <Switch
                    className="scale-75 -ml-1"
                    checked={!!settings.permissions?.[k]}
                    disabled={!settings.ai_enabled}
                    onCheckedChange={(v) => save({ permissions: { ...settings.permissions, [k]: v } })}
                    data-testid={`ai-perm-switch-${k}`}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-red-700 mb-2">🔴 Technisch gesperrt (API-Schicht)</div>
            <div className="space-y-1.5">
              {LOCKED_LABELS.map((l) => (
                <div key={l} className="flex items-center gap-2 text-xs text-red-800" data-testid={`ai-locked-${l}`}>
                  <Lock className="w-3.5 h-3.5" /> {l}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-red-600 mt-2">Die KI kann Budgets & Zahlungen NIE aendern — die API verweigert es, unabhaengig vom Prompt.</p>
          </div>
        </div>

        <button
          data-testid="toggle-ai-log-btn"
          onClick={() => setShowLog((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium hover:underline"
        >
          <History className="w-3.5 h-3.5" /> KI-Protokoll ({actions.length})
          {showLog ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
        {showLog && (
          <div className="space-y-1.5" data-testid="ai-actions-log">
            {actions.length === 0 && <p className="text-xs text-[var(--studio-text-muted)]">Noch keine KI-Aktionen protokolliert.</p>}
            {actions.map((a) => (
              <div key={a.id} className="flex items-start gap-2 border border-[var(--studio-border)] rounded-lg px-3 py-2 text-xs" data-testid={`ai-action-${a.id}`}>
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2">
                    {new Date(a.timestamp).toLocaleString("de-CH")} · {a.platform} · {a.action}
                    {a.reverted && <Badge variant="outline" className="text-[9px]">rueckgaengig</Badge>}
                  </div>
                  <div className="text-[var(--studio-text-muted)] mt-0.5">{a.reason}</div>
                </div>
                {a.revert && !a.reverted && (
                  <Button
                    size="sm" variant="outline" className="h-6 text-[10px] gap-1"
                    disabled={reverting === a.id}
                    onClick={() => revert(a)}
                    data-testid={`revert-action-${a.id}`}
                  >
                    {reverting === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Rueckgaengig
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdsManagerPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [overview, setOverview] = useState(null);
  const [datePreset, setDatePreset] = useState("last_30d");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recos, setRecos] = useState(null);
  const [allAccounts, setAllAccounts] = useState(null);
  const [cfilter, setCfilter] = useState("");
  const [autopilot, setAutopilot] = useState("off");
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${API}/ads/autopilot`, { params: { company_id: companyId }, withCredentials: true })
      .then((r) => setAutopilot(r.data.mode || "off")).catch(() => setAutopilot("off"));
  }, [companyId]);
  const changeAutopilot = async (mode) => {
    try {
      const r = await axios.post(`${API}/ads/autopilot`, { company_id: companyId, mode }, { withCredentials: true });
      setAutopilot(mode);
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Autopilot-Einstellung fehlgeschlagen"); }
  };
  useEffect(() => { setCfilter(overview?.campaign_filter || ""); }, [overview?.campaign_filter]);
  const saveFilter = async () => {
    try {
      const r = await axios.post(`${API}/ads/campaign-filter`, { company_id: companyId, filter: cfilter }, { withCredentials: true });
      toast.success(r.data.message);
      loadOverview(companyId, datePreset);
    } catch (e) { toast.error(e.response?.data?.detail || "Filter speichern fehlgeschlagen"); }
  };
  const [recosLoading, setRecosLoading] = useState(false);
  const [executingReco, setExecutingReco] = useState(null);
  const [leadsInfo, setLeadsInfo] = useState(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiSettings, setAiSettings] = useState(null);
  const aiEnabled = aiSettings?.ai_enabled !== false;

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => {
        const list = r.data || [];
        setCompanies(list);
        if (list.length > 0) setCompanyId(list[0].id);
      })
      .catch(() => {});
  }, []);

  const loadStatus = useCallback(async (cid) => {
    setStatus(null); setAccounts(null); setOverview(null); setRecos(null); setLeadsInfo(null); setError("");
    if (!cid) return;
    try {
      const r = await axios.get(`${API}/ads/status`, { params: { company_id: cid }, withCredentials: true });
      setStatus(r.data);
      if (r.data.connected && r.data.has_ads_scope && !r.data.selected_ad_account_id) {
        const ar = await axios.get(`${API}/ads/accounts`, { params: { company_id: cid }, withCredentials: true });
        setAccounts(ar.data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Status konnte nicht geladen werden");
    }
  }, []);

  const loadOverview = useCallback(async (cid, preset) => {
    if (!cid) return;
    setLoading(true); setError("");
    try {
      const r = await axios.get(`${API}/ads/overview`, {
        params: { company_id: cid, date_preset: preset }, withCredentials: true,
      });
      setOverview(r.data);
      axios.get(`${API}/ads/leads`, { params: { company_id: cid }, withCredentials: true })
        .then((lr) => setLeadsInfo(lr.data)).catch(() => {});
    } catch (e) {
      setError(e.response?.data?.detail || "Daten konnten nicht geladen werden");
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(companyId); }, [companyId, loadStatus]);
  useEffect(() => {
    if (status?.connected && status?.has_ads_scope && status?.selected_ad_account_id) {
      loadOverview(companyId, datePreset);
    }
  }, [status, datePreset, companyId, loadOverview]);

  const selectAccount = async (acc) => {
    try {
      await axios.post(`${API}/ads/accounts/select`, {
        company_id: companyId, ad_account_id: acc.id, ad_account_name: acc.name,
      }, { withCredentials: true });
      toast.success(`Werbekonto "${acc.name}" zugewiesen`);
      loadStatus(companyId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen");
    }
  };

  const fetchRecos = async () => {
    setRecosLoading(true);
    try {
      const r = await axios.post(`${API}/ads/ai/recommendations`, {
        company_id: companyId, date_preset: datePreset,
      }, { withCredentials: true });
      setRecos(r.data.recommendations || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Analyse fehlgeschlagen");
    } finally {
      setRecosLoading(false);
    }
  };

  const executeReco = async (r, i) => {
    setExecutingReco(i);
    try {
      const res = await axios.post(`${API}/ads/ai/recommendations/execute`, {
        company_id: companyId, campaign_id: r.campaign_id,
        action: r.suggested_action, reason: r.title,
      }, { withCredentials: true });
      toast.success(res.data.message);
      setRecos((prev) => prev.filter((_, idx) => idx !== i));
      loadOverview(companyId, datePreset);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Umsetzen fehlgeschlagen");
    } finally {
      setExecutingReco(null);
    }
  };

  const currency = overview?.account?.currency || "CHF";
  const summary = overview?.summary;

  return (
    <div className="p-6 space-y-5" data-testid="ads-manager-page">
      <PageHelp
        title="Ads Manager"
        text="Steuere deine Meta-Werbekampagnen direkt hier: Budgets anpassen, Kampagnen pausieren/aktivieren, Leads & Kosten verfolgen. Die KI analysiert deine Performance und erstellt auf Wunsch komplette Kampagnen — immer pausiert, du gibst frei."
      />
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Target className="w-6 h-6 text-[var(--studio-primary,#2563EB)]" />
          <h1 className="text-2xl font-bold">Ads Manager</h1>
        </div>
        <div className="flex-1" />
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-56" data-testid="ads-company-select"><SelectValue placeholder="Firma wählen" /></SelectTrigger>
          <SelectContent>
            {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={datePreset} onValueChange={setDatePreset}>
          <SelectTrigger className="w-40" data-testid="ads-date-preset-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="last_7d">Letzte 7 Tage</SelectItem>
            <SelectItem value="last_14d">Letzte 14 Tage</SelectItem>
            <SelectItem value="last_30d">Letzte 30 Tage</SelectItem>
            <SelectItem value="last_90d">Letzte 90 Tage</SelectItem>
          </SelectContent>
        </Select>
        {status?.selected_ad_account_id && (
          <Button variant="outline" size="icon" onClick={() => loadOverview(companyId, datePreset)} title="Aktualisieren" data-testid="ads-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Connection gates */}
      {status && !status.connected && (
        <Card className="border-amber-300" data-testid="ads-not-connected">
          <CardContent className="pt-5 pb-4 flex flex-wrap items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            <div className="flex-1 text-sm">
              <strong>Meta ist für diese Firma nicht verbunden.</strong> Verbinde zuerst das Meta-Konto, um Werbekampagnen zu verwalten.
            </div>
            <Button onClick={() => navigate("/social-media")} data-testid="goto-social-btn">Zu Social Media</Button>
          </CardContent>
        </Card>
      )}
      {status && status.connected && !status.has_ads_scope && (
        <Card className="border-amber-300" data-testid="ads-missing-scope">
          <CardContent className="pt-5 pb-4 flex flex-wrap items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            <div className="flex-1 text-sm">
              <strong>Neue Berechtigungen erforderlich.</strong> Für den Ads Manager braucht die Verbindung zusätzlich <code>ads_management</code> und <code>ads_read</code>. Bitte Meta einmal <strong>neu verbinden</strong> — die neuen Berechtigungen werden dann automatisch abgefragt.
            </div>
            <Button onClick={() => navigate("/social-media")} data-testid="reconnect-meta-btn">Meta neu verbinden</Button>
          </CardContent>
        </Card>
      )}

      {/* AI guardrails panel */}
      {companyId && status?.connected && <AiControlPanel companyId={companyId} onSettingsChange={setAiSettings} />}

      {/* Ad account selection */}
      {status?.connected && status?.has_ads_scope && !status?.selected_ad_account_id && accounts && (
        <Card data-testid="ads-account-picker">
          <CardHeader className="pb-2"><CardTitle className="text-base">Werbekonto auswählen</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {accounts.accounts?.length === 0 && (
              <div className="text-sm text-[var(--studio-text-muted)]">Keine Werbekonten gefunden. Erstelle eines im <a className="underline" href="https://business.facebook.com" target="_blank" rel="noreferrer">Meta Business Manager</a>.</div>
            )}
            {accounts.accounts?.map((a) => (
              <div key={a.id} className="flex items-center gap-3 border border-[var(--studio-border)] rounded-lg p-3" data-testid={`ad-account-row-${a.id}`}>
                <div className="flex-1">
                  <div className="font-medium text-sm">{a.name}</div>
                  <div className="text-xs text-[var(--studio-text-muted)]">{a.id} · {a.currency} · Ausgaben bisher: {fmtMoney((a.amount_spent || 0) / 100, a.currency)}</div>
                </div>
                <Button size="sm" onClick={() => selectAccount(a)} data-testid={`select-ad-account-${a.id}`}>Zuweisen</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {error && status?.selected_ad_account_id && (
        <Card className="border-red-300"><CardContent className="pt-4 pb-3 text-sm text-red-600" data-testid="ads-error">{error}</CardContent></Card>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[var(--studio-text-muted)]" data-testid="ads-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade Kampagnendaten von Meta…
        </div>
      )}

      {/* Dashboard */}
      {overview && !loading && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--studio-text-muted)]" data-testid="ads-account-info">
            Werbekonto: <strong>{overview.account.name}</strong> ({overview.account.id}) · Währung {currency}
            {allAccounts ? (
              <Select onValueChange={(id) => { const acc = allAccounts.find((a) => a.id === id); if (acc) selectAccount(acc); }}>
                <SelectTrigger className="h-7 w-80 text-xs" data-testid="account-switch-select"><SelectValue placeholder="Anderes Werbekonto wählen…" /></SelectTrigger>
                <SelectContent>
                  {allAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.id}) · {a.currency}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" data-testid="account-switch-btn"
                onClick={async () => {
                  try {
                    const r = await axios.get(`${API}/ads/accounts`, { params: { company_id: companyId }, withCredentials: true });
                    setAllAccounts(r.data.accounts || []);
                  } catch (e) { toast.error(e.response?.data?.detail || "Konten laden fehlgeschlagen"); }
                }}>
                Konto wechseln
              </Button>
            )}
            <span className="inline-flex items-center gap-1" title="Bei geteiltem Konto: nur Kampagnen mit diesem Begriff zeigen (mit «-Begriff» ausschliessen, Komma für mehrere). Neue KI-Kampagnen bekommen den Begriff automatisch als Präfix.">
              <Input className="h-7 w-48 text-xs" placeholder="Kampagnen-Filter, z.B. BellaStrada" value={cfilter}
                onChange={(e) => setCfilter(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveFilter()}
                data-testid="meta-campaign-filter-input" />
              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={saveFilter} data-testid="meta-campaign-filter-save">
                Filter speichern
              </Button>
            </span>
            {overview.campaign_filter && (
              <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-300" data-testid="meta-filter-active">
                Gefiltert: {overview.campaign_filter} — Kosten/KPIs nur für diese Kampagnen
              </Badge>
            )}
          </div>
          {overview.account.status && overview.account.status !== 1 && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 flex flex-wrap items-center gap-2" data-testid="ads-payment-warning">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>
                <b>Werbekonto-Problem:</b> {overview.account.status === 3
                  ? "Zahlungsfehler (offene Rechnung) — Meta liefert keine Anzeigen aus, bis die Zahlungsmethode aktualisiert ist."
                  : overview.account.status === 2
                    ? "Konto deaktiviert — bitte im Meta Business Manager prüfen."
                    : `Konto-Status ${overview.account.status} — bitte im Meta Business Manager prüfen.`}
              </span>
              <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-700"
                onClick={() => window.open(`https://business.facebook.com/billing_hub/payment_settings?asset_id=${overview.account.id.replace("act_", "")}`, "_blank", "noopener")}
                data-testid="ads-payment-fix-btn">
                Zahlungsmethode bei Meta öffnen
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard testid="kpi-spend" icon={Coins} label="Ausgaben" value={fmtMoney(summary?.spend, currency)} />
            <KpiCard testid="kpi-impressions" icon={Eye} label="Impressionen" value={fmtNum(summary?.impressions)} />
            <KpiCard testid="kpi-clicks" icon={MousePointerClick} label="Klicks" value={fmtNum(summary?.clicks)} sub={summary?.cpc ? `CPC ${fmtMoney(summary.cpc, currency)}` : null} />
            <KpiCard testid="kpi-ctr" icon={TrendingUp} label="CTR" value={summary?.ctr ? `${Number(summary.ctr).toFixed(2)}%` : "—"} />
            <KpiCard testid="kpi-leads" icon={UserCheck} label="Leads" value={fmtNum(summary?.leads)} />
            <KpiCard testid="kpi-cpl" icon={Coins} label="Kosten/Lead" value={summary?.cost_per_lead ? fmtMoney(summary.cost_per_lead, currency) : "—"} />
          </div>

          {/* AI panel */}
          <div className="flex flex-wrap gap-2 items-center">
            <Button className="gap-2" onClick={() => setAiDialogOpen(true)} disabled={!aiEnabled} data-testid="open-ai-create-btn">
              <Sparkles className="w-4 h-4" /> KI-Kampagne erstellen
            </Button>
            <Button variant="outline" className="gap-2" onClick={fetchRecos} disabled={recosLoading || !aiEnabled} data-testid="ai-recommendations-btn">
              {recosLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
              KI-Empfehlungen
            </Button>
            <span className="inline-flex items-center gap-1.5 text-xs" title="Täglich prüft der Autopilot deine Kampagnen gegen die Pausier-Regeln (einstellbar unten). Vorschlagen = nur E-Mail. Vollautomatik = schwache Kampagnen werden automatisch pausiert und auf Wunsch neue Entwürfe zur Freigabe erstellt.">
              🤖 Autopilot
              <Select value={autopilot} onValueChange={changeAutopilot}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="autopilot-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Aus</SelectItem>
                  <SelectItem value="suggest">Täglich vorschlagen</SelectItem>
                  <SelectItem value="auto">Vollautomatik (pausieren + erstellen)</SelectItem>
                </SelectContent>
              </Select>
            </span>
            {!aiEnabled && <span className="text-xs text-red-600" data-testid="ai-disabled-hint">KI-Marketing ist deaktiviert (Not-Aus)</span>}
          </div>

          {recos && (
            <Card data-testid="ai-recommendations-panel">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> KI-Empfehlungen</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {recos.map((r, i) => {
                  const style = SEVERITY_STYLE[r.severity] || SEVERITY_STYLE.info;
                  const canExecute = (r.suggested_action === "pause" || r.suggested_action === "activate") && r.campaign_id;
                  const isBudget = ["increase_budget", "decrease_budget"].includes(r.suggested_action);
                  return (
                    <div key={i} className={`border rounded-lg p-3 text-sm ${style.cls}`} data-testid={`reco-item-${i}`}>
                      <div className="font-medium">{style.icon} {r.title}</div>
                      <div className="text-xs mt-1 text-gray-700">{r.recommendation}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {canExecute && (
                          <Button
                            size="sm" variant="outline" className="h-7 text-xs gap-1"
                            disabled={executingReco === i}
                            onClick={() => executeReco(r, i)}
                            data-testid={`reco-execute-${i}`}
                          >
                            {executingReco === i ? <Loader2 className="w-3 h-3 animate-spin" /> : r.suggested_action === "pause" ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                            Umsetzen: {r.suggested_action === "pause" ? "Pausieren" : "Aktivieren"}
                          </Button>
                        )}
                        {isBudget && (
                          <span className="flex items-center gap-1 text-[11px] text-red-700" data-testid={`reco-budget-locked-${i}`}>
                            <Lock className="w-3 h-3" /> Budget: nur manuell (KI technisch gesperrt)
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Autopilot: Regeln & Freigaben */}
          <MetaAutopilotPanel
            companyId={companyId}
            currency={currency}
            autopilotMode={autopilot}
            onChanged={() => loadOverview(companyId, datePreset)}
          />

          {/* Campaigns */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Megaphone className="w-4 h-4" /> Kampagnen ({overview.campaigns.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="campaigns-list">
              {overview.campaigns.length === 0 && (
                <div className="text-sm text-[var(--studio-text-muted)] py-4 text-center" data-testid="no-campaigns">
                  Noch keine Kampagnen in diesem Werbekonto. Starte mit <strong>„KI-Kampagne erstellen"</strong> oben.
                </div>
              )}
              {overview.campaigns.map((c) => (
                <CampaignRow key={c.id} c={c} companyId={companyId} currency={currency} datePreset={datePreset} onChanged={() => loadOverview(companyId, datePreset)} />
              ))}
            </CardContent>
          </Card>

          {/* Leads */}
          {leadsInfo && leadsInfo.forms?.length > 0 && (
            <Card data-testid="leads-panel">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><UserCheck className="w-4 h-4" /> Lead-Formulare ({leadsInfo.page_name})</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {leadsInfo.forms.map((f) => (
                  <div key={f.id} className="flex items-center gap-3 text-sm border-b border-[var(--studio-border)] last:border-0 py-1.5" data-testid={`lead-form-${f.id}`}>
                    <div className="flex-1">{f.name}</div>
                    <Badge variant="outline" className="text-[10px]">{f.status}</Badge>
                    <span className="text-xs font-semibold">{fmtNum(f.leads_count)} Leads</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      <AiCreateDialog
        open={aiDialogOpen}
        onOpenChange={setAiDialogOpen}
        companyId={companyId}
        currency={currency}
        onCreated={() => loadOverview(companyId, datePreset)}
      />
    </div>
  );
}
